// Phase 1C — tile publication reducer.
//
// Extracted so GridViewer AND unit tests both call the exact same code.
// Rules (see Phase 1C spec §5 and reviewer P0-6/P0-8):
//   - seq-guard: a late response with an obsolete requestSeq NEVER overwrites
//     the current tile state. This is what protects against the true A/B race.
//   - hasSnapshot is explicit. Empty tile that successfully loaded is a real
//     snapshot. Snapshot existence is NOT inferred from parcels.length.
//   - abort preserves the previous snapshot (never wipes drawn parcels).
//   - error preserves the previous snapshot AND records refreshError.
//   - ready atomically replaces (parcels + range meta + reconciled + status).
import type { DataStatus, Parcel, TileRecord } from "./types";

export type TileFetchKind = "ready" | "error" | "aborted";

export interface TileFetchResult {
  kind: TileFetchKind;
  parcels?: Parcel[];
  asOfBlockMin?: number | null;
  asOfBlockMax?: number | null;
  reconciled?: boolean;
  dataStatus?: DataStatus | null;
  error?: string | null;
}

export interface PublishInput {
  requestSeq: number;
  result: TileFetchResult;
  now: number;
}

/** Bump seq + mark the tile as refreshing while keeping the previous snapshot. */
export function beginRefresh(prev: TileRecord, nextSeq: number): TileRecord {
  return {
    ...prev,
    requestSeq: nextSeq,
    state: prev.hasSnapshot ? "stale" : "loading",
    refreshError: null,
    // preserve everything else (parcels, asOfBlock*, reconciled, dataStatus, hasSnapshot)
  };
}

/**
 * Apply a tile publication with seq-guard.
 * If input.requestSeq !== prev.requestSeq the publication is DROPPED.
 * This is the sole place tile state changes on fetch completion.
 */
export function applyPublication(prev: TileRecord, input: PublishInput): TileRecord {
  if (input.requestSeq !== prev.requestSeq) return prev;
  const { result, now } = input;

  if (result.kind === "aborted") {
    // Abort never destroys a prior snapshot. If none existed, back to "missing".
    return {
      ...prev,
      state: prev.hasSnapshot ? "ready" : "missing",
      refreshError: null,
    };
  }

  if (result.kind === "error") {
    if (prev.hasSnapshot) {
      return {
        ...prev,
        state: "stale",
        refreshError: result.error ?? "unknown",
        fetchedAt: now,
      };
    }
    return {
      ...prev,
      state: "error",
      error: result.error ?? "unknown",
      refreshError: null,
      fetchedAt: now,
    };
  }

  // Ready: atomic replace.
  return {
    ...prev,
    state: "ready",
    parcels: result.parcels ?? [],
    hasSnapshot: true,
    asOfBlockMin: result.asOfBlockMin ?? null,
    asOfBlockMax: result.asOfBlockMax ?? null,
    reconciled: !!result.reconciled,
    dataStatus: result.dataStatus ?? null,
    fetchedAt: now,
    error: null,
    refreshError: null,
  };
}
